import {JSONPath} from 'jsonpath-plus';
import cloneDeep from 'lodash/cloneDeep';

export interface Matched {
    path: string[],
    parentProperty: string,
    value: string,
    highlight: string,
}

type ActionType = "menu" | "action" | "integration";
type ResultType = 'value' | 'path' | 'pointer' | 'parent' | 'parentProperty' | 'all';

export interface Found {
    stepId: string,
    stepType: ActionType,
    nodeType: string,
    matched: Matched[];
}

export interface SearchSettings {
    caseSensitive?: boolean,
    wordMatch?: boolean,
    stepNumber?: boolean,
    variable?: boolean,
}

export interface JSONPathResults {
    path: string
    value: string
    parent: any
    parentProperty: string
    hasArrExpr: boolean
    pointer: string
}

export interface SearchError {
    error: string
}

export interface ReplaceAllSteps {
    stepId: string,
    step: any
    found: Found
}

interface StepData {
    stepId: string,
    stepType: ActionType,
    nodeType: string,
}

const SEARCH_MIN_LIMIT: number = 3;

const JSON_PATH_PATTERN: string = `$..*`;
const RESULT_TYPE: ResultType = "all";

const EXCLUDE_KEY = [
    'menuType',
    'type',
    'menu',
    'stepUniqueID',
    'menuParams',
    'results',
    'items',
    'groups',
    'metadata',
    'actionType',
    'actionParams',
    'result',
    'action',

    'reportingLabels',
    'reportingFeatures',
    'reportingStepLabel',
    "nodeVersion",
];

export class FindAndReplace {

    private flow: any;
    private steps: any = {};

    constructor(flow: any, steps?: string[]) {
        this.setFlow(flow, steps);
    }

    public search(searchFor: string, settings?: SearchSettings): Found[] | SearchError {

        searchFor = this.escapeRegExp(searchFor);

        if (!this.flow && !this.steps) {
            return {error: "No results found"};
        }

        if (settings.stepNumber) {
            return this.searchForStep(searchFor, settings)
        }

        if (searchFor.length < SEARCH_MIN_LIMIT) {
            return {error: `Minimum of ${SEARCH_MIN_LIMIT} characters is required`};
        }

        return this.searchAll(searchFor, settings)
    }

    private replaceStep(replaceValue: string, paths: string[], parentProperty: string, flow: {}) {
        let step = flow

        for (const path of paths) {
            step = step[path]
        }
        step[parentProperty] = replaceValue
    }

    public replace(stepId: string, searchFor: string, replaceWith: string, match: Matched, settings?: SearchSettings): any {

        const step = this.steps[stepId]

        searchFor = this.escapeRegExp(searchFor);

        const patter: RegExp = this.searchPattern(searchFor, settings);
        const {path, parentProperty} = match;

        const replaceValue: string = match.value.replaceAll(patter, replaceWith)
        this.replaceStep(replaceValue, path.slice(2, -1), parentProperty, step);

        return step;
    }

    public replaceAll(searchFor: string, replaceWith: string, found: Found[], settings?: SearchSettings): ReplaceAllSteps[] {

        const flow = cloneDeep(this.steps)

        searchFor = this.escapeRegExp(searchFor);

        const patter: RegExp = this.searchPattern(searchFor, settings);

        found
            .map(found => found.matched)
            .reduce((accumulator, currentValue) => accumulator.concat(currentValue), [])
            .forEach(({value, path, parentProperty}) => {
                const replaceValue: string = value.replaceAll(patter, replaceWith)
                this.replaceStep(replaceValue, path.slice(1, -1), parentProperty, flow)
            });

        return found.map(found => ({
            step: flow[found.stepId],
            stepId: found.stepId,
            found: found
        }));
    }

    public undoAll(): any {
        return this.flow;
    }

    public undoStep(stepId) {
        return this.flow[stepId];
    }

    private searchPattern(searchFor: string, settings?: SearchSettings) {

        let flags: string = "gi";
        let pattern: string = `${searchFor}`;

        if (settings !== undefined) {
            if (settings?.wordMatch) {
                pattern = `\\b${searchFor}\\b`
            }

            if (settings?.variable && !(searchFor.startsWith("\\[\\[") && searchFor.endsWith("\\]\\]"))) {
                pattern = `\\[\\[${searchFor}\\]\\]`
            }

            if (settings?.caseSensitive) {
                flags = "g"
            }
        }

        return new RegExp(pattern, flags);
    }

    private searchForStep(searchFor: string, settings?: SearchSettings): Found[] {

        const found: Found[] = [];
        const keys: string[] = Object.keys(this.steps);

        const regExp: RegExp = this.searchPattern(searchFor, settings)

        for (const key of keys) {
            if (this.isMatch(key, regExp)) {
                const stepData: StepData = this.getStepData(key)
                found.push({
                    matched: [],
                    nodeType: stepData.nodeType,
                    stepId: stepData.stepId,
                    stepType: stepData.stepType
                })
            }
        }

        return found.sort(sortStep);
    }

    private searchAll(searchFor: string, settings: SearchSettings): Found[] {

        const results: { [index: string]: Found } = {};

        const regExp: RegExp = this.searchPattern(searchFor, settings)

        const matched: JSONPathResults[] = JSONPath({
            path: JSON_PATH_PATTERN,
            json: this.steps,
            resultType: RESULT_TYPE
        })
            .filter(response => !EXCLUDE_KEY.includes(response.parentProperty))
            .filter(response => this.isMatch(response.value, regExp))

        for (let match of matched) {
            const path = match.pointer.split("/");
            const stepId: string = path[1]

            const matched: Matched = {
                value: match.value,
                parentProperty: match.parentProperty,
                path,
                highlight: this.highlight(searchFor, match.value, regExp)
            }

            if (stepId in results) {
                results[stepId].matched.push(matched)
                continue;
            }

            const stepData: StepData = this.getStepData(stepId);
            results[stepId] = {
                matched: [matched],
                nodeType: stepData.nodeType,
                stepId: stepData.stepId,
                stepType: stepData.stepType
            }

        }

        return Object.values(results).sort(sortStep);
    }

    private getStepData(stepId: string): StepData {
        const step = this.steps[stepId]

        if (step?.menu) {
            return {
                stepId,
                stepType: "menu",
                nodeType: step?.menu.menuType
            }
        }

        if (step?.action) {
            return {
                stepId,
                stepType: "action",
                nodeType: step?.action.actionType
            }
        }

        if (step?.integration) {
            return {
                stepId,
                stepType: "integration",
                nodeType: step?.integration.integrationType
            }
        }

        return {
            stepId,
            stepType: step?.type,
            nodeType: step?.type
        }
    }

    private isMatch(text: string, regExp: RegExp): boolean {
        if (typeof text === "string") {
            return text.match(regExp) !== null;
        }

        return false;
    }

    private escapeRegExp(searchFor: string) {
        return searchFor.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    private highlight(searchFor: string, value: string, regExp: RegExp): string {
        return value.replaceAll(regExp, `<span class="highlight-text">${searchFor}</span>`)
    }

    public setFlow(flow: any, steps?: string[]): void {
        if (Object.keys(flow).length === 1) {
            return
        }

        const cloneFlow = cloneDeep(flow);
        this.flow = cloneFlow
        if (steps && steps.length >= 1) {
            Object.keys(cloneFlow)
                .filter(step => steps.includes(step))
                .forEach(step => {
                    if (step in cloneFlow) {
                        this.steps[step] = cloneFlow[step]
                    }
                });
        } else {
            this.steps = cloneFlow
        }
    }
}

const sortStep = (a: Found, b: Found): number => {

    const stepA = +a.stepId.replace("step", "")
    const stepB = +b.stepId.replace("step", "")

    if (stepA < stepB) {
        return -1;
    } else if (stepA > stepB) {
        return 1;
    }

    return 0;
}
